import React, {
    useCallback, useState, useEffect, useContext
} from 'react';
import _ from 'lodash';
import { useModal } from '@jutro/components';
import { withRouter } from 'react-router-dom';
import { useDependencies } from 'gw-portals-dependency-react';
import { useTranslator } from '@jutro/locale';
import { messages as commonMessages } from 'gw-platform-translations';
import { WizardPage, wizardProps } from 'gw-portals-wizard-react';
import { useValidation } from 'gw-portals-validation-react';
import { ViewModelForm, ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import { fnolCommonMessages } from 'gw-capability-fnol-common-react';
import { ErrorCodes } from 'gw-portals-util-adac-js';
import AdacDigitalWizardPageTemplate from '../../templates/AdacDigitalWizardPageTemplate';
import FNOLContext from '../../components/Context/Context';
import metadata from './AdacCallbackOptionsPage.metadata.json5';
import styles from './AdacCallbackOptionsPage.module.scss';
import wizardMessages from '../../FNOLAdacWizard.messages';
import messages from './AdacCallbackOptionsPage.messages';
import { INSURED_ROLE_CODE, REPORTER_ROLE_CODE } from '../../constants/RoleCodes';

function AdacCallbackOptionsPage(props) {
    const {
        wizardData: claimVM,
        updateWizardData,
        history
    } = props;
    const { onValidate, isComponentValid } = useValidation(
        'AdacCallbackOptionsPage'
    );
    const viewModelService = useContext(ViewModelServiceContext);
    const fnolContext = useContext(FNOLContext);
    const { actionOption } = fnolContext;
    const translator = useTranslator();
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [callbackHours, setCallbackHours] = useState();
    const { showAlert } = useModal();

    const { FNOLService } = useDependencies('FNOLService');

    const writePortalRecallOptionHours = useCallback((value) => {
        setUpFnolOption(claimVM, INSURED_ROLE_CODE);
        _.set(claimVM.value, 'callbackHours', value);

        const updatedContacts = claimVM.value.contacts.map((contact) => {
            if (contact.policyRole === INSURED_ROLE_CODE) {
                _.set(contact, 'portalRecallOption_Adac.hours', value);
                return contact;
            }
            return contact;
        });
        _.set(claimVM.value, 'contacts', updatedContacts);
        setCallbackHours(value);
    }, [claimVM.value]);

    const setUpFnolOption = useCallback((claim, role) => {
        const portalRecallOptionVM = viewModelService.create(
            {},
            'cc',
            'adac.edge.capabilities.claim.fnol.dto.PortalRecallOptionDTO'
        );
        const fnolOptionsTypesArray = portalRecallOptionVM.option.aspects.availableValues;

        const PortalRecallOption = _.find(fnolOptionsTypesArray, (co) => {
            return co.code === actionOption;
        });
        _.set(portalRecallOptionVM, 'option.value', PortalRecallOption);

        const updatedContacts = claim.value.contacts.map((c) => (c.policyRole === role
            ? { ...c, portalRecallOption_Adac: portalRecallOptionVM.value } : c));
        _.set(claim, 'contacts', updatedContacts);

    }, [actionOption, viewModelService]);


    useEffect(() => {
        const storedCallbackHours = _.get(claimVM.value, 'callbackHours');
    
        if (storedCallbackHours) {
            setCallbackHours(storedCallbackHours);
        }
    }, [claimVM]);


    const onNext = useCallback(() => {
        setIsLoading(true);
        setLoadingMessage(translator(fnolCommonMessages.fnolSubmitClaimDetails));
        return FNOLService.getUnverifiedFNOLDetails(claimVM.value)
            .then((response) => {
                claimVM.value = response;
                return history.push({
                    pathname: '/adac-confirmation',
                    state: {
                        callbackOption: translator(messages[`callback${callbackHours}`])
                    }
                });
            })
            .catch((err) => {
                const { appErrorCode, baseError } = err;
                if (appErrorCode === ErrorCodes.APPLICATION_ERROR && baseError === undefined) {
                    history.push({ pathname: '/callcenter' });
                } else {
                    showAlert({
                        title: fnolCommonMessages.fnolErrorSubmitClaimTitle,
                        message: fnolCommonMessages.fnolErrorSubmitClaimMessage,
                        status: 'error',
                        icon: 'mi-error-outline',
                        confirmButtonText: commonMessages.ok
                    }).catch(() => {
                        return _.noop;
                    });
                }
                return false;
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [translator, claimVM, FNOLService, history, callbackHours]);

    const overrides = {
        callOptionsCard: {
            value: callbackHours
        },
        adacCallbackOptionsPageLoader: {
            loaded: !isLoading,
            loadingMessage: translator(loadingMessage)
        },
        adacCallbackOptionsContainer: {
            visible: !isLoading
        },
        navButtons: {
            visible: false
        },
        sentContainer: {
            visible: false
        }
    };
    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            writePortalRecallOptionHours
        }
    };
    
    return (
        <WizardPage
            onNext={onNext}
            showCancel={false}
            nextLabel={wizardMessages.next}
            previousLabel={wizardMessages.previous}
            disableNext={!isComponentValid}
            template={AdacDigitalWizardPageTemplate}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={claimVM}
                overrideProps={overrides}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
            />
        </WizardPage>
    );
}
AdacCallbackOptionsPage.propTypes = wizardProps;
export default withRouter(AdacCallbackOptionsPage);
